import React, { useEffect, useState } from "react";
import { ChevronRight, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getContestHistory } from "../../../../services/contestService";
import { getContestReturnUrl } from "./contestNavigation";

const FILTERS = ["All", "Wins", "Lost", "Pending"];

const formatDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const getHistoryDateValue = (item) =>
  formatDateInputValue(
    item?.date ||
      item?.contestDate ||
      item?.startTime ||
      item?.createdOn ||
      item?.createdAt,
  );

const ContestHistory = ({ dateFilter = "" }) => {
  const [activeTab, setActiveTab] = useState("All");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contestData, setContestData] = useState([]);
  const [stats, setStats] = useState({
    totalEarnings: 0,
    wins: 0,
    avgScore: 0,
    streak: 0,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await getContestHistory();

        if (res?.success) {
          setContestData(res.result?.history || []);
          setStats(
            res.result?.stats || {
              totalEarnings: 0,
              wins: 0,
              avgScore: 0,
              streak: 0,
            }
          );
        } else {
          setError(res?.message || "Failed to load contest history.");
        }
      } catch (err) {
        console.error(err);
        setError("An error occurred while fetching your history.");
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, []);

  const getStatusLabel = (status) => {
    if (status === "Won" || status === "Win" || status === "Wins") {
      return "Wins";
    }

    return status || "Pending";
  };

  const filteredData = contestData.filter((item) => {
    const matchesStatus =
      activeTab === "All" || getStatusLabel(item.status) === activeTab;
    const matchesDate = !dateFilter || getHistoryDateValue(item) === dateFilter;

    return matchesStatus && matchesDate;
  });

  const formatDate = (date) => {
    if (!date) return "";

    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) {
      return date;
    }

    return parsedDate.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getPrize = (item) =>
    item.prize || item.prizeCoin || item.earnedCoins || item.reward || 0;

  const getEntry = (item) =>
    item.entry || item.entryCoin || item.entryFee || item.entryCoins || 0;

  const openResult = (item) => {
    const contestId = item?.contestId || item?._id || item?.id;
    if (!contestId) return;

    navigate("/dash/contest-winner", {
      state: {
        contestId,
        showAnswerKey: item?.showAnswerKey,
        returnUrl: getContestReturnUrl(),
      },
    });
  };

  const renderContestCard = (item, index) => {
    const statusLabel = getStatusLabel(item.status);
    const contestId = item.contestId || item._id || item.id;
    const isResultAvailable = statusLabel !== "Pending";

    return (
      <div
        key={item.id || item._id || `${item.title}-${index}`}
        className="overflow-hidden rounded-[24px] bg-white shadow-[0_8px_22px_rgba(44,25,60,0.16)] ring-1 ring-black/5"
      >
        <div className="space-y-2 p-2 lg:p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h3 className="text-left text-xl font-extrabold text-black lg:text-xl">
                {item.title || item.contestName || "Contest"}
              </h3>

              <p className="mt-1 text-base text-gray-400 lg:text-sm">
                {formatDate(item.date || item.createdAt)}
              </p>
            </div>

            <button
              type="button"
              disabled={!isResultAvailable}
              onClick={() => {
                if (isResultAvailable) {
                  openResult(item);
                }
              }}
              className={`flex shrink-0 items-center gap-1 rounded-2xl px-4 py-2 text-md font-bold text-white transition lg:text-sm ${
                isResultAvailable
                  ? "bg-[#562566] hover:bg-[#451d52]"
                  : "cursor-not-allowed bg-[#F5A623] text-[#562566]"
              }`}
            >
              {isResultAvailable ? "Result" : "Pending"}
              {isResultAvailable && (
                <ChevronRight size={22} className="lg:h-4 lg:w-4" />
              )}
            </button>
          </div>

          <div className="my-5 h-px bg-gray-200" />

          <div className="grid grid-cols-3 text-center">
            <div>
              <p className="text-base text-gray-500 lg:text-sm">Prize</p>
              <p className="mt-2 text-md font-sm text-[#D9A12F] lg:text-base">
                {getPrize(item)}
              </p>
            </div>

            <div>
              <p className="text-base text-gray-500 lg:text-sm">Rank</p>
              <p className="mt-2 text-md font-sm text-[#D9A12F] lg:text-base">
                {item.rank || "-"}
              </p>
            </div>

            <div>
              <p className="text-base text-gray-500 lg:text-sm">Entry</p>
              <p className="mt-2 text-md font-sm text-[#D9A12F] lg:text-base">
                {getEntry(item)}
              </p>
            </div>
          </div>
        </div>

        <div
          className={`py-3 text-center text-xl font-bold text-white lg:text-base ${
            statusLabel === "Lost" ? "bg-[#8B3A45]" : "bg-[#562566]"
          }`}
        >
          {statusLabel}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white pb-20 text-black lg:min-h-screen lg:px-8 lg:py-8">
      <div className="mx-auto max-w-[400px] space-y-2 lg:max-w-[1180px] lg:space-y-7">
        {/* Top Reward Card */}
        <div className="rounded-[24px] bg-white p-4 shadow-[0_6px_18px_rgba(0,0,0,0.14)] ring-1 ring-black/5 lg:p-6">
          <div className="flex items-start justify-between gap-2 lg:items-center">
            <div className="lg:flex lg:items-center lg:gap-4">
              <div className="mb-7 flex h-12 w-12 items-end gap-1  lg:mb-0 lg:h-16 lg:w-20">
                <span className="h-8 w-3 rounded-t-[4px] bg-[#562566] lg:h-12 lg:w-5" />
                <span className="h-12 w-3.5 rounded-t-[4px] bg-[#562566] lg:h-16 lg:w-5" />
                <span className="h-6 w-3 rounded-t-[4px] bg-[#562566] lg:h-13 lg:w-5" />
              </div>

              <div>
                <h3 className="text-lg font-bold text-black lg:text-xl">
                  Total Contest Reward
                </h3>

                <p className="mt-1 text-lg font-bold text-left text-[#F5A623] lg:text-xl">
                  {stats.totalEarnings || 0} Credits
                </p>
              </div>
            </div>

            <div className="pt-1 text-right lg:border-l lg:border-gray-200 lg:pl-16">
              <p className="text-lg text-gray-500 lg:text-base">
                Contest Join
              </p>
              <p className="text-xl font-bold text-black lg:text-3xl">
                {contestData.length || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Desktop Grid */}
        <div className="lg:grid lg:grid-cols-[1fr_1fr] lg:gap-7">
          {/* Left Section */}
          <div className="space-y-2 lg:space-y-5">
            <div className="lg:rounded-[24px] lg:bg-white lg:p-6 lg:shadow-[0_6px_18px_rgba(0,0,0,0.10)] lg:ring-1 lg:ring-black/5">
              <h2 className="m-4 text-center text-xl font-bold text-[#562566] lg:m-0 lg:mb-5 lg:text-left">
                Performance Overview
              </h2>

              <div className="overflow-hidden rounded-[22px] bg-[#F5A623] text-black">
                <div className="grid grid-cols-3 divide-x divide-white/35 py-2 text-center lg:py-4">
                  <div>
                    <p className="text-xl font-extrabold leading-none">
                      {stats.wins || 0}
                    </p>
                    <p className="mt-2 text-sm font-extrabold uppercase">
                      Wins
                    </p>
                  </div>

                  <div>
                    <p className="text-xl font-extrabold leading-none">
                      {stats.avgScore || 0}
                    </p>
                    <p className="mt-2 text-sm font-extrabold uppercase">
                      Avg Score
                    </p>
                  </div>

                  <div>
                    <p className="text-xl font-extrabold leading-none">
                      {stats.streak || 0}
                    </p>
                    <p className="mt-2 text-sm font-extrabold uppercase">
                      Streak
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-4 rounded-[20px] bg-[#F2ECF8] p-1.5">
                <div className="grid grid-cols-4 gap-1">
                  {FILTERS.map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setActiveTab(tab)}
                      className={`rounded-[16px] py-2 text-base font-semibold transition lg:text-sm ${
                        activeTab === tab
                          ? "bg-[#562566] text-white shadow-sm"
                          : "text-black"
                      }`}
                    >
                      {tab}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="pt-12 text-center text-gray-400">Loading...</div>
            ) : error ? (
              <div className="pt-12 text-center text-red-500">{error}</div>
            ) : filteredData.length === 0 ? (
              <div className="pt-12 text-center text-gray-400">
                <Clock className="mx-auto mb-2" />
                <p>No contest history yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredData.map((item, index) =>
                  renderContestCard(item, index)
                )}
              </div>
            )}
          </div>

          {/* Right Section - Desktop Only */}
          <div className="mt-6 hidden rounded-[24px] bg-white p-6 shadow-[0_6px_18px_rgba(0,0,0,0.10)] ring-1 ring-black/5 lg:mt-0 lg:block">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-2xl font-extrabold text-[#562566]">
                Recent Contest Activity
              </h2>

              <button
                type="button"
                onClick={() => setActiveTab("All")}
                className="rounded-lg border border-[#562566] px-4 py-2 text-sm font-semibold text-[#562566] transition hover:bg-[#562566] hover:text-white"
              >
                View All
              </button>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-100">
              <div className="grid grid-cols-4 bg-[#FBFAFC] px-4 py-3 text-sm font-bold text-gray-500">
                <p>Contest Name</p>
                <p>Date</p>
                <p>Prize</p>
                <p>Status</p>
              </div>

              {contestData.length === 0 ? (
                <div className="px-4 py-12 text-center text-gray-400">
                  No recent activity
                </div>
              ) : (
                contestData.slice(0, 6).map((item, index) => {
                  const statusLabel = getStatusLabel(item.status);

                  return (
                    <div
                      key={item.id || item._id || `activity-${index}`}
                      className="grid grid-cols-4 items-center border-t border-gray-100 px-4 py-4 text-sm"
                    >
                      <p className="truncate font-semibold text-black">
                        {item.title || item.contestName || "Contest"}
                      </p>

                      <p className="text-gray-500">
                        {formatDate(item.date || item.createdOn)}
                      </p>

                      <p className="font-semibold text-[#F5A623]">
                        {getPrize(item)}
                      </p>

                      <p>
                        <span
                          className={`rounded-full px-4 py-1.5 text-xs font-bold ${
                            statusLabel === "Lost"
                              ? "bg-red-50 text-red-600"
                              : statusLabel === "Pending"
                              ? "bg-yellow-50 text-yellow-700"
                              : "bg-green-50 text-green-700"
                          }`}
                        >
                          {statusLabel}
                        </span>
                      </p>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContestHistory;
